import React from "react";
import { api } from "../../lib/api";
import PlayerRoutines from "../PlayerRoutines";

const GOLD = "var(--org-accent)";
const fmtDate = (d) => { const x = new Date(d); return isNaN(x) ? d : x.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); };

function MediaTile({ playerId, m }) {
  const src = m.attachmentId ? api.shootingMediaUrl(playerId, m.attachmentId) : m.url;
  const yt = m.kind === "link" && /youtu\.?be/.test(m.url || "") ? (m.url.match(/(?:v=|youtu\.be\/|shorts\/)([\w-]{6,})/) || [])[1] : null;
  return (
    <figure className="border border-white/10 bg-[#141414] overflow-hidden" data-testid={`shooting-media-${m.id}`}>
      <div className="bg-black aspect-video flex items-center justify-center">
        {m.kind === "image" && <img src={src} alt={m.name || "shot"} className="max-h-full max-w-full object-contain" crossOrigin="anonymous" />}
        {m.kind === "video" && <video src={src} controls playsInline className="max-h-full max-w-full" />}
        {m.kind === "link" && (yt ? <iframe title={m.name || "video"} src={`https://www.youtube.com/embed/${yt}`} className="w-full h-full" allowFullScreen /> : <a href={m.url} target="_blank" rel="noreferrer" className="text-sm underline" style={{ color: GOLD }}>{m.name || m.url}</a>)}
      </div>
      {m.note && <figcaption className="px-3 py-2 text-xs text-[#c0c0c0] leading-snug">{m.note}</figcaption>}
    </figure>
  );
}

// Read view styled after the coach's one-pager: Analysis (Observations · Limitations) left, numbered Shooting Keys right, media below.
export const ShootingAssessmentView = React.forwardRef(function ShootingAssessmentView({ a, playerName, playerId, context }, ref) {
  return (
    <div ref={ref} className="report-print bg-[#0f0f0f] border border-white/10" data-testid="shooting-assessment-view">
      <div className="px-6 pt-6 pb-4 text-center">
        <div className="font-heading uppercase text-2xl md:text-3xl text-white tracking-tight">Development Priority 1 · <span className="relative inline-block px-2"><span className="relative z-10" style={{ color: GOLD }}>{a.title || "Shooting"}</span><span className="absolute inset-0 rounded-[50%] border-2 -rotate-2" style={{ borderColor: GOLD, opacity: 0.6 }} /></span></div>
      </div>
      <div className="py-2 text-center font-heading uppercase text-base md:text-lg tracking-[0.2em] text-[#121212]" style={{ background: GOLD }} data-testid="shooting-assessment-player-bar">{playerName}{context ? ` · ${context}` : ""}</div>
      <div className="px-6 py-2 flex flex-wrap justify-center gap-x-6 gap-y-1 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] border-b border-white/10">
        <span data-testid="shooting-assessment-date">Assessed {fmtDate(a.date)}</span><span>·</span><span data-testid="shooting-assessment-coach">By {a.coach}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-0">
        <div className="p-6 lg:border-r lg:border-dotted lg:border-white/25">
          <div className="font-heading uppercase text-lg text-white text-center mb-3">Analysis</div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="text-center py-2" style={{ background: GOLD }}><div className="font-heading uppercase text-sm text-[#121212]">Observations</div><div className="text-[10px] text-[#121212]/80">What we see</div></div>
            <div className="text-center py-2" style={{ background: GOLD }}><div className="font-heading uppercase text-sm text-[#121212]">Limitations</div><div className="text-[10px] text-[#121212]/80">Why it's a problem</div></div>
          </div>
          <div className="space-y-4">
            {a.topics.map(t => (
              <div key={t.id} className="relative grid grid-cols-2 gap-3" data-testid={`shooting-topic-${t.id}`}>
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-8" style={{ background: GOLD }} />
                <div className="border-2 p-4 text-center" style={{ borderColor: `${"#d8b854"}99` }}>
                  <div className="text-base font-bold text-white mb-1">{t.title}</div>
                  <div className="text-sm text-[#d0d0d0] leading-snug">{t.observation}</div>
                </div>
                <div className="border-2 p-4 text-center" style={{ borderColor: `${"#d8b854"}99` }}>
                  <div className="text-base font-bold text-white mb-1">{t.title}</div>
                  <div className="text-sm text-[#d0d0d0] leading-snug">{t.limitation}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="p-6">
          <div className="font-heading uppercase text-lg text-white text-center">Shooting Keys Based on Analysis</div>
          <div className="text-xs text-[#a0a0a0] text-center mb-4">What we want to reinforce in the shot</div>
          <div className="space-y-4">
            {a.keys.map((k, i) => (
              <div key={k.id} className="border-2 p-4 flex gap-4" style={{ borderColor: `${"#d8b854"}99` }} data-testid={`shooting-key-${k.id}`}>
                <div className="font-heading text-4xl leading-none text-white -rotate-6 shrink-0 w-10">{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-center text-base font-bold text-white uppercase">{k.name}</div>
                  {k.cue && <div className="text-center text-sm font-bold text-white">CUE: ‘{k.cue}’</div>}
                  {k.points.length > 0 && <ol className="list-decimal pl-5 mt-2 space-y-1 text-sm text-[#d0d0d0] leading-snug">{k.points.map((p, j) => <li key={j}>{p}</li>)}</ol>}
                </div>
              </div>
            ))}
          </div>
          {a.footer && <p className="mt-4 text-sm italic text-[#c0c0c0] leading-snug" data-testid="shooting-footer">**{a.footer}</p>}
        </div>
      </div>

      {a.media?.length > 0 && (
        <div className="px-6 pb-6" data-testid="shooting-media">
          <div className="font-heading uppercase text-lg text-white mb-3 border-t border-white/10 pt-5">Film &amp; Photos</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{a.media.map(m => <MediaTile key={m.id} playerId={playerId} m={m} />)}</div>
        </div>
      )}

      <div className="px-6 pb-6 border-t border-white/10 pt-5" data-testid="shooting-routine-block">
        <PlayerRoutines playerId={playerId} playerName={playerName} onlyType="shooting" embedded />
      </div>
    </div>
  );
});
